"use client";

import React from "react";
import {
  Search,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@/app/_module/components/ui/button";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";
import { useToast } from "@/app/_module/hooks/use-toast";

import ConfirmDeleteModal from "./confirmModal";

export interface ColumnDef<T> {
  key: keyof T | "actions";
  label: string;
  render?: (item: T) => React.ReactNode;
}

interface ResourceManagementTableProps<T extends { id: string }> {
  title?: string;
  data: T[];
  isLoading: boolean;
  columns: ColumnDef<T>[];
  onDelete?: (id: string) => Promise<void>;
  canDelete?: (item: T) => boolean;
  renderCreateModal?: () => React.JSX.Element;
  renderEditModal?: (item: T) => React.JSX.Element | null;
}

const ResourceManagementTable = function<T extends { id: string }>({
  title = "Resources",
  data,
  isLoading,
  columns,
  onDelete,
  canDelete,
  renderCreateModal,
  renderEditModal,
}: ResourceManagementTableProps<T>) {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [resourceToDelete, setResourceToDelete] = useState<string | null>(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  const itemsPerPage = 10;

  const filteredData = data.filter((item) => {
    const term = search.toLowerCase();
    return Object.values(item).some(
      (val) => typeof val === "string" && val.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(startIndex, startIndex + itemsPerPage);

  // Get items that can be deleted based on canDelete function
  const deletableItems = currentItems.filter((item) =>
    canDelete ? canDelete(item) : true
  );

  const handleSelectAll = (checked: boolean) => {
    // Only select items that can be deleted
    setSelected(checked ? deletableItems.map((d) => d.id) : []);
  };

  const handleSelect = (id: string, checked: boolean) => {
    setSelected((prev) =>
      checked ? [...prev, id] : prev.filter((sid) => sid !== id)
    );
  };

  const handleBulkDelete = async () => {
    if (!onDelete || selected.length === 0) return;

    try {
      // Delete all selected items
      await Promise.all(selected.map((id) => onDelete(id)));

      toast({
        title: "Deleted",
        description: `Successfully deleted ${selected.length} resource${selected.length !== 1 ? 's' : ''}`
      });

      setSelected([]);
      setShowBulkDeleteConfirm(false);
    } catch {
      toast({
        title: "Error",
        description: "Failed to delete some resources",
        variant: "destructive"
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (resourceToDelete && onDelete) {
      try {
        await onDelete(resourceToDelete);
        toast({ title: "Deleted", description: "Resource deleted successfully" });
      } catch {
        toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
      }
      setResourceToDelete(null);
    }
  };

  return (
    <div className="mt-8 w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold">{title}</h2>
          {selected.length > 0 && (
            <Button
              onClick={() => setShowBulkDeleteConfirm(true)}
              size="sm"
              variant="destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {selected.length} selected
            </Button>
          )}
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <input
            className="border rounded px-3 py-2 w-full md:w-64"
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            value={search}
          />
          {renderCreateModal?.()}
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl shadow-sm border w-full overflow-hidden">
        <div className="overflow-x-auto w-full">
          {isLoading ? (
            <div className="p-6 text-center">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={deletableItems.length > 0 && selected.length === deletableItems.length}
                      onCheckedChange={handleSelectAll}
                      disabled={deletableItems.length === 0}
                    />
                  </TableHead>
                  {columns.map((col) => (
                    <TableHead key={col.key.toString()}>{col.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentItems.map((item) => {
                  const isDeletable = canDelete ? canDelete(item) : true;
                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Checkbox
                          checked={selected.includes(item.id)}
                          onCheckedChange={(checked) => handleSelect(item.id, !!checked)}
                          disabled={!isDeletable}
                        />
                      </TableCell>
                    {columns.map((col) => (
                      <TableCell key={col.key.toString()}>
                        {col.key === "actions" ? (
                          <div className="flex gap-1 items-center">
                            {renderEditModal?.(item)}
                            {!!onDelete && (canDelete ? canDelete(item) : true) && (
                              <Button
                                onClick={() => setResourceToDelete(item.id)}
                                size="icon"
                                variant="ghost"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        ) : col.render ? (
                          col.render(item)
                        ) : (
                          (item[col.key] as React.ReactNode) ?? "-"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t gap-4 flex-wrap">
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredData.length)} of {filteredData.length}
            </div>
            <div className="flex items-center gap-2">
              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                size="sm"
                variant="outline"
              >
                <ChevronLeft className="w-4 h-4" />
                Prev
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  className="w-8 h-8 p-0"
                  onClick={() => setCurrentPage(page)}
                  size="sm"
                  variant={currentPage === page ? "default" : "outline"}
                >
                  {page}
                </Button>
              ))}
              <Button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                size="sm"
                variant="outline"
              >
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredData.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No items found</p>
            <p className="text-sm">Try adjusting your search terms</p>
          </div>
        )}
      </div>

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        onConfirm={handleDeleteConfirm}
        onOpenChange={(open) => !open && setResourceToDelete(null)}
        open={!!resourceToDelete}
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmDeleteModal
        onConfirm={handleBulkDelete}
        onOpenChange={(open) => !open && setShowBulkDeleteConfirm(false)}
        open={showBulkDeleteConfirm}
        title="Delete Multiple Resources"
        description={`Are you sure you want to delete ${selected.length} resource${selected.length !== 1 ? 's' : ''}? This action cannot be undone.`}
      />
    </div>
  );
}

export default ResourceManagementTable;
